import { useState } from "react";
import {
  Button,
  Chip,
  Combobox,
  NumberInput,
  SectionCard,
  ToggleInput,
  useToast,
} from "@a2z/ui";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { ProductVariantView, ProductView } from "@/lib/api/types";
import { useUpdateProductMutation } from "@/modules/products/services/useUpdateProductMutation";
import { useUpdateVariantMutation } from "@/modules/products/services/useVariantMutations";
import { ProductVariantsEditorSheet } from "./ProductVariantsEditorSheet";
import { BoxIcon, SparkleIcon } from "./product-edit-icons";

type ProductEditSidebarProps = {
  storeSlug: string;
  product: ProductView;
  defaultVariant: ProductVariantView | null;
};

const LOW_STOCK_THRESHOLD = 10;

/** Trioty card chrome applied to the sidebar cards on mobile (≤lg) only. */
const MOBILE_CARD_CHROME =
  "max-lg:!rounded-[18px] max-lg:!border-[color:var(--hair)] max-lg:!bg-[color:var(--soft)] max-lg:[&>header]:!items-center max-lg:[&>header>span]:hidden max-lg:[&>header>div:first-of-type>span:nth-child(2)]:hidden";

/**
 * Right-rail sidebar on the Edit Product redesign. Publishing visibility,
 * Adjust stock, and Mark-out-of-stock are live controls that save
 * instantly via the update-product mutation — no shared form state, no
 * save button needed.
 */
export function ProductEditSidebar({
  storeSlug,
  product,
  defaultVariant,
}: ProductEditSidebarProps) {
  const t = useTranslations();
  const toast = useToast();
  const { mutateAsync: updateProduct, isPending: isSaving } =
    useUpdateProductMutation();
  const { mutateAsync: updateVariant, isPending: isSavingStock } =
    useUpdateVariantMutation();

  // Local mirrors of the product state so toggles feel responsive while
  // the mutation is in flight. Reset when the product updates.
  const [visible, setVisible] = useState(product.isActive);
  const [forceOutOfStock, setForceOutOfStock] = useState(
    defaultVariant?.forceOutOfStock ?? false,
  );
  const [stockInput, setStockInput] = useState(
    String(defaultVariant?.stockCount ?? 0),
  );
  const [stockEditorOpen, setStockEditorOpen] = useState(false);

  // Per-product inventory overrides. null threshold / null overselling = inherit
  // the store default; the tri-state overselling maps default|allow|block.
  const [lowStockOverride, setLowStockOverride] = useState(
    product.lowStockThreshold !== null,
  );
  const [lowStockValue, setLowStockValue] = useState(
    String(product.lowStockThreshold ?? LOW_STOCK_THRESHOLD),
  );
  const [prevLowStock, setPrevLowStock] = useState(product.lowStockThreshold);
  if (product.lowStockThreshold !== prevLowStock) {
    setPrevLowStock(product.lowStockThreshold);
    setLowStockOverride(product.lowStockThreshold !== null);
    setLowStockValue(String(product.lowStockThreshold ?? LOW_STOCK_THRESHOLD));
  }

  const oversellMode: "default" | "allow" | "block" =
    product.allowOverselling === null
      ? "default"
      : product.allowOverselling
        ? "allow"
        : "block";
  const [oversell, setOversell] = useState(oversellMode);
  const [prevOversell, setPrevOversell] = useState(product.allowOverselling);
  if (product.allowOverselling !== prevOversell) {
    setPrevOversell(product.allowOverselling);
    setOversell(oversellMode);
  }

  const unlimitedMode: "default" | "on" | "off" =
    product.unlimitedStock === null
      ? "default"
      : product.unlimitedStock
        ? "on"
        : "off";
  const [unlimited, setUnlimited] = useState(unlimitedMode);
  const [prevUnlimited, setPrevUnlimited] = useState(product.unlimitedStock);
  if (product.unlimitedStock !== prevUnlimited) {
    setPrevUnlimited(product.unlimitedStock);
    setUnlimited(unlimitedMode);
  }
  // Unlimited stock is never out of stock and never reserved/decremented, so
  // the count, low-stock warning/threshold, mark-out-of-stock, and overselling
  // controls are all meaningless — disable them while it's on.
  const isUnlimited = unlimited === "on";

  // Re-sync the mirrors during render when the server values change —
  // previous-value comparison pattern instead of setState-in-effect.
  const [prevIsActive, setPrevIsActive] = useState(product.isActive);
  if (product.isActive !== prevIsActive) {
    setPrevIsActive(product.isActive);
    setVisible(product.isActive);
  }
  const serverForceOutOfStock = defaultVariant?.forceOutOfStock ?? false;
  const [prevForceOutOfStock, setPrevForceOutOfStock] = useState(
    serverForceOutOfStock,
  );
  if (serverForceOutOfStock !== prevForceOutOfStock) {
    setPrevForceOutOfStock(serverForceOutOfStock);
    setForceOutOfStock(serverForceOutOfStock);
  }
  const serverStockCount = defaultVariant?.stockCount ?? 0;
  const [prevStockCount, setPrevStockCount] = useState(serverStockCount);
  if (serverStockCount !== prevStockCount) {
    setPrevStockCount(serverStockCount);
    setStockInput(String(serverStockCount));
  }

  // Effective visibility mirrors the hero summary: the toggle is necessary
  // but not sufficient — a product is only Published when it's active AND
  // has photos AND has at least one purchasable variant. Showing the same
  // logic here (with the blocking reason inline) prevents the confusing
  // case where the toggle says "ON" but the hero still says "Hidden".
  const hiddenReasons = product.sellerBuyerHiddenReasons ?? [];
  const isPublished =
    visible &&
    !hiddenReasons.includes("missing_images") &&
    !hiddenReasons.includes("no_active_purchasable_variants");
  const statusLabel = isPublished
    ? t(keys.products.editSidebarPublishingPublished)
    : !visible
      ? t(keys.products.editSidebarPublishingHidden)
      : hiddenReasons.includes("missing_images")
        ? t(keys.products.editSidebarPublishingHiddenMissingImages)
        : t(keys.products.editSidebarPublishingHiddenOutOfStock);

  // Inventory summary reflects the WHOLE product, not just the default
  // variant — for multi-variant products the default variant's count is only
  // a slice of the real on-hand total (the bug this card used to show).
  const isMultiVariant = product.variants.length > 1;
  const stockCount = product.variants.reduce(
    (sum, variant) => sum + variant.stockCount,
    0,
  );
  const stockPercent = Math.min(100, Math.max(0, (stockCount / 200) * 100));
  // Honor the per-product override the seller can set below; fall back to the
  // module default (which matches the store default) when unset. Mirrors
  // deriveProductStatus in cardAttention.ts.
  const effectiveLowStock = product.lowStockThreshold ?? LOW_STOCK_THRESHOLD;
  const stockTone: "success" | "accent" | "danger" =
    stockCount === 0
      ? "danger"
      : stockCount < effectiveLowStock
        ? "accent"
        : "success";
  const stockToneBar =
    stockTone === "danger"
      ? "bg-[color:var(--error-500)]"
      : stockTone === "accent"
        ? "bg-[color:var(--accent-500)]"
        : "bg-[color:var(--success-500)]";
  const stockLabelText =
    stockCount === 0
      ? t(keys.products.editSidebarInventoryOutOfStock)
      : stockCount < effectiveLowStock
        ? t(keys.products.editSidebarInventoryLowStock)
        : t(keys.products.editSidebarInventoryInStock);

  const buildVariantPayload = (overrides: {
    stockCount?: number;
    forceOutOfStock?: boolean;
  }) => ({
    price: defaultVariant?.price ?? 0,
    discountedPrice: defaultVariant?.discountedPrice ?? null,
    stockCount: overrides.stockCount ?? defaultVariant?.stockCount ?? 0,
    forceOutOfStock:
      overrides.forceOutOfStock ?? defaultVariant?.forceOutOfStock ?? false,
  });

  const handleVisibilityChange = async (next: boolean) => {
    setVisible(next);
    try {
      await updateProduct({
        storeSlug,
        productId: product.id,
        isActive: next,
      });
      toast.success(
        next ? t(keys.products.toastShown) : t(keys.products.toastHidden),
      );
    } catch {
      setVisible(!next);
    }
  };

  const handleForceOutOfStockChange = async (next: boolean) => {
    setForceOutOfStock(next);
    try {
      await updateProduct({
        storeSlug,
        productId: product.id,
        initialVariant: buildVariantPayload({ forceOutOfStock: next }),
      });
      toast.success(t(keys.products.toastStockUpdated));
    } catch {
      setForceOutOfStock(!next);
    }
  };

  // Stock is saved explicitly (button / Enter), never on blur — sellers asked
  // for a deliberate commit. It targets the specific variant by id (the single
  // live variant, which the lifecycle keeps as the default), not a generic
  // "default variant" rewrite.
  const persistedStock = defaultVariant?.stockCount ?? 0;
  const parsedStock = Number(stockInput);
  const isStockDirty =
    stockInput.trim() !== "" &&
    Number.isFinite(parsedStock) &&
    parsedStock >= 0 &&
    Math.floor(parsedStock) !== persistedStock;

  const handleStockSave = async () => {
    if (!isStockDirty || !defaultVariant || isSavingStock) {
      return;
    }
    try {
      await updateVariant({
        storeSlug,
        productId: product.id,
        variantId: defaultVariant.id,
        fields: { stockCount: Math.floor(parsedStock) },
      });
      toast.success(t(keys.products.toastStockUpdated));
    } catch {
      setStockInput(String(persistedStock));
    }
  };

  const parsedLowStock = Number(lowStockValue);
  const isLowStockValid =
    lowStockValue.trim() !== "" &&
    Number.isFinite(parsedLowStock) &&
    parsedLowStock >= 0;
  const isLowStockDirty =
    isLowStockValid && Math.floor(parsedLowStock) !== product.lowStockThreshold;

  const saveLowStockThreshold = async (value: number | null) => {
    try {
      await updateProduct({
        storeSlug,
        productId: product.id,
        lowStockThreshold: value,
      });
      toast.success(t(keys.products.toastStockUpdated));
    } catch {
      // Re-sync from server on failure.
      setLowStockOverride(product.lowStockThreshold !== null);
      setLowStockValue(String(product.lowStockThreshold ?? LOW_STOCK_THRESHOLD));
    }
  };

  const handleLowStockOverrideToggle = (on: boolean) => {
    setLowStockOverride(on);
    if (!on) void saveLowStockThreshold(null);
  };

  const handleLowStockSave = () => {
    if (!isLowStockValid) return;
    void saveLowStockThreshold(Math.floor(parsedLowStock));
  };

  const handleOversellChange = async (mode: "default" | "allow" | "block") => {
    const previous = oversell;
    setOversell(mode);
    const value = mode === "default" ? null : mode === "allow";
    try {
      await updateProduct({
        storeSlug,
        productId: product.id,
        allowOverselling: value,
      });
      toast.success(t(keys.products.toastStockUpdated));
    } catch {
      setOversell(previous);
    }
  };

  const handleUnlimitedChange = async (mode: "default" | "on" | "off") => {
    const previous = unlimited;
    setUnlimited(mode);
    const value = mode === "default" ? null : mode === "on";
    try {
      await updateProduct({
        storeSlug,
        productId: product.id,
        unlimitedStock: value,
      });
      toast.success(t(keys.products.toastStockUpdated));
    } catch {
      setUnlimited(previous);
    }
  };

  return (
    <aside className="flex flex-col gap-4">
      {/* Publishing */}
      <SectionCard
        variant="flat"
        className={MOBILE_CARD_CHROME}
        title={t(keys.products.editSidebarPublishing)}
        icon={<SparkleIcon />}
      >
        <div className="flex flex-col gap-3.5">
          <div className="flex items-center gap-2 rounded-[11px] border border-[color:var(--border-light)] bg-[color:var(--surface-alt)] px-3 py-2.5">
            <span
              aria-hidden="true"
              className={`h-2 w-2 shrink-0 rounded-full ${
                isPublished
                  ? "bg-[color:var(--success-500)]"
                  : "bg-[color:var(--warning-500)]"
              }`}
            />
            <span className="text-[13px] font-semibold text-[color:var(--text-primary)]">
              {statusLabel}
            </span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 flex-col">
              <span className="text-meta text-[color:var(--text-primary)]">
                {t(keys.products.editSidebarPublishingVisible)}
              </span>
              <span className="text-chip text-[color:var(--text-muted)]">
                {t(keys.products.editSidebarPublishingVisibleHint)}
              </span>
            </div>
            <ToggleInput
              name="product-sidebar-visible"
              checked={visible}
              disabled={isSaving}
              onChange={(event) => {
                void handleVisibilityChange(event.target.checked);
              }}
              ariaLabel={t(keys.products.editSidebarPublishingVisible)}
            />
          </div>
          {/* TODO(new-soon): Schedule changes toggle (publish at future date/time).
              Hidden until the backend exposes a scheduled-publishing field. */}
        </div>
      </SectionCard>

      {/* Inventory */}
      <SectionCard
        variant="flat"
        className={MOBILE_CARD_CHROME}
        title={t(keys.products.editSidebarInventory)}
        icon={<BoxIcon />}
      >
        <div className="flex flex-col gap-3">
          <div className="flex items-baseline gap-2">
            <span className="font-mono text-[1.75rem] font-bold leading-none tracking-tight text-[color:var(--text-primary)]">
              {isUnlimited ? "∞" : stockCount}
            </span>
            <span className="text-meta text-[color:var(--text-muted)]">
              {t(keys.products.editSidebarInventoryUnitsTotal)}
            </span>
            <Chip
              tone={
                isUnlimited
                  ? "success"
                  : stockTone === "danger"
                    ? "danger"
                    : stockTone === "accent"
                      ? "accent"
                      : "success"
              }
              size="sm"
              className="ms-auto"
            >
              {isUnlimited
                ? t(keys.products.editSidebarInventoryUnlimited)
                : stockLabelText}
            </Chip>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-[color:var(--border-light)]">
            <div
              className={`h-full ${
                isUnlimited ? "bg-[color:var(--success-500)]" : stockToneBar
              }`}
              style={{ width: `${isUnlimited ? 100 : stockPercent}%` }}
            />
          </div>
          {isMultiVariant ? (
            // Multi-variant: a single field can't represent per-option stock,
            // so offer a quick per-variant stock editor right here in the
            // inventory card.
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={isUnlimited}
              onClick={() => setStockEditorOpen(true)}
            >
              {t(keys.products.variantsEditStock)}
            </Button>
          ) : (
            <>
              <div className="flex flex-col gap-1">
                <span className="text-meta text-[color:var(--text-primary)]">
                  {t(keys.products.editSidebarInventoryAdjustStock)}
                </span>
                <div
                  className="flex items-center gap-2"
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      void handleStockSave();
                    }
                  }}
                >
                  <span className="w-[88px] shrink-0">
                    <NumberInput
                      name="product-sidebar-stock"
                      value={stockInput}
                      onChange={(event) => setStockInput(event.target.value)}
                      placeholder="0"
                      className="text-center"
                      disabled={isUnlimited}
                    />
                  </span>
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    disabled={!isStockDirty || isUnlimited}
                    isLoading={isSavingStock}
                    onClick={() => void handleStockSave()}
                  >
                    {t(keys.common.save)}
                  </Button>
                </div>
                <span className="text-chip text-[color:var(--text-muted)]">
                  {t(keys.products.editSidebarInventoryAdjustStockHint)}
                </span>
              </div>
              <div className="flex items-center justify-between gap-3 border-t border-[color:var(--border-light)] pt-3.5">
                <span className="text-meta text-[color:var(--text-primary)]">
                  {t(keys.products.editSidebarInventoryMarkOutOfStock)}
                </span>
                <ToggleInput
                  name="product-sidebar-mark-oos"
                  checked={forceOutOfStock}
                  disabled={isSaving || isUnlimited}
                  onChange={(event) => {
                    void handleForceOutOfStockChange(event.target.checked);
                  }}
                  ariaLabel={t(keys.products.editSidebarInventoryMarkOutOfStock)}
                />
              </div>
            </>
          )}
          {/* Per-product low-stock threshold override (else inherit store default). */}
          <div className="flex flex-col gap-2 border-t border-[color:var(--border-light)] pt-3.5">
            <div className="flex items-center justify-between gap-3">
              <span className="text-meta text-[color:var(--text-primary)]">
                {t(keys.products.editSidebarLowStockOverride)}
              </span>
              <ToggleInput
                name="product-sidebar-lowstock-override"
                checked={lowStockOverride}
                disabled={isSaving || isUnlimited}
                onChange={(event) =>
                  handleLowStockOverrideToggle(event.target.checked)
                }
                ariaLabel={t(keys.products.editSidebarLowStockOverride)}
              />
            </div>
            {lowStockOverride ? (
              <div
                className="flex items-center gap-2"
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    handleLowStockSave();
                  }
                }}
              >
                <span className="w-[88px] shrink-0">
                  <NumberInput
                    name="product-sidebar-lowstock"
                    value={lowStockValue}
                    onChange={(event) => setLowStockValue(event.target.value)}
                    placeholder="10"
                    className="text-center"
                    disabled={isUnlimited}
                  />
                </span>
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  disabled={!isLowStockDirty || isUnlimited}
                  isLoading={isSaving}
                  onClick={handleLowStockSave}
                >
                  {t(keys.common.save)}
                </Button>
              </div>
            ) : (
              <span className="text-chip text-[color:var(--text-muted)]">
                {t(keys.products.editSidebarLowStockUsingDefault)}
              </span>
            )}
          </div>

          {/* Per-product overselling override (default | allow | block). */}
          <div className="flex flex-col gap-1.5 border-t border-[color:var(--border-light)] pt-3.5">
            <span className="text-meta text-[color:var(--text-primary)]">
              {t(keys.products.editSidebarOversellLabel)}
            </span>
            <Combobox
              value={oversell}
              disabled={isUnlimited}
              onChange={(value) =>
                void handleOversellChange(
                  (value ?? "default") as "default" | "allow" | "block",
                )
              }
              options={[
                { value: "default", label: t(keys.products.editSidebarOversellDefault) },
                { value: "allow", label: t(keys.products.editSidebarOversellAllow) },
                { value: "block", label: t(keys.products.editSidebarOversellBlock) },
              ]}
              aria-label={t(keys.products.editSidebarOversellLabel)}
            />
          </div>

          {/* Per-product unlimited-stock override (default | on | off). */}
          <div className="flex flex-col gap-1.5 border-t border-[color:var(--border-light)] pt-3.5">
            <span className="text-meta text-[color:var(--text-primary)]">
              {t(keys.products.editSidebarUnlimitedLabel)}
            </span>
            <Combobox
              value={unlimited}
              onChange={(value) =>
                void handleUnlimitedChange(
                  (value ?? "default") as "default" | "on" | "off",
                )
              }
              options={[
                { value: "default", label: t(keys.products.editSidebarUnlimitedDefault) },
                { value: "on", label: t(keys.products.editSidebarUnlimitedOn) },
                { value: "off", label: t(keys.products.editSidebarUnlimitedOff) },
              ]}
              aria-label={t(keys.products.editSidebarUnlimitedLabel)}
            />
          </div>
        </div>
      </SectionCard>

      {/* TODO(new-soon): Last-7-days analytics card (views / add-to-cart /
          orders / conv-rate sparkline). Orders count is computable from the
          orders list, but views and add-to-cart need a tracking pipeline we
          don't have. Re-enable when analytics events land. */}

      {/* TODO(new-soon): Tags card. Hidden until product tags are supported
          on the backend. */}

      {stockEditorOpen ? (
        <ProductVariantsEditorSheet
          isOpen
          mode="stock"
          onClose={() => setStockEditorOpen(false)}
          storeSlug={storeSlug}
          product={product}
        />
      ) : null}
    </aside>
  );
}
